/**
 * Conversation Summarizer for Farm Manager
 *
 * Maintains condensed conversation history to preserve context
 * while staying within token limits. Extracts key topics, entities,
 * and actions from conversation for context injection.
 */

/**
 * Configuration for summarization
 */
const SUMMARIZER_CONFIG = {
        // Number of messages before triggering summarization
        SUMMARY_THRESHOLD: 8,
        // Keep last N messages in full detail
        RECENT_MESSAGES_TO_KEEP: 6,
        // Maximum number of key points to extract
        MAX_KEY_POINTS: 5,
        // Maximum referenced animals to track
        MAX_TRACKED_ANIMALS: 10,
};

/**
 * Topic detection patterns
 */
const TOPIC_PATTERNS = {
        health: /health|sick|ill|fever|treatment|symptom|disease|score|suspicious|diagnos|medic|vet/i,
        breeding: /calf|calving|colostrum|weaning|vaccination|lifecycle|breed|pregnancy|pregnant|birth/i,
        devices: /sensor|bionode|device|connect|signal|data|monitor|network|gateway/i,
        performance: /kpi|performance|production|rate|metric|trend|analys|report|benchmark/i,
        tasks: /task|work|schedule|planner|assign|complete|pending|remind/i,
        feeding: /feed|water|intake|consumption|bioflow|drink|nutrition|appetite/i,
        groups: /group|pen|barn|section|location|move|transfer/i,
};

/**
 * Extracts topics and entities from a single message
 * @param {Object} message - Message object with content/text
 * @returns {Object} Extracted topics and entities
 */
function extractFromMessage(message) {
        const text = message.content || message.text || "";
        const topics = new Set();
        const animals = new Set();
        const groups = new Set();
        const actions = [];

        // Detect topics
        Object.entries(TOPIC_PATTERNS).forEach(([topic, pattern]) => {
                if (pattern.test(text)) {
                        topics.add(topic);
                }
        });

        // Extract animal IDs (alphanumeric patterns like MAC addresses or IDs)
        // Common patterns: 12-char MAC, 4-8 char IDs, numeric IDs
        const animalPatterns = [
                /\b([A-F0-9]{12})\b/gi, // MAC addresses
                /\b([A-Z]{2,4}[0-9]{2,6})\b/g, // ID patterns like CA1234
                /(?:animal|calf)\s*(?:id)?[:# ]*([A-Z0-9]{3,12})/gi, // "Animal ID: XXX" or "Calf ID: XXX"
        ];

        animalPatterns.forEach((pattern) => {
                const matches = text.matchAll(pattern);
                for (const match of matches) {
                        if (match[1] && match[1].length >= 3 && match[1].length <= 12) {
                                animals.add(match[1].toUpperCase());
                        }
                }
        });

        // Extract group names (common patterns)
        const groupPatterns = [
                /\b(?:group|pen|barn)[:# ]*([A-Za-z0-9\s-]+?)(?:\s*[,.\n]|$)/gi,
                /\b(newborn|pre-weaning|weaning|post-weaning|grower|quarantine|breeding)\b/gi,
        ];

        groupPatterns.forEach((pattern) => {
                const matches = text.matchAll(pattern);
                for (const match of matches) {
                        if (match[1]) {
                                groups.add(match[1].trim());
                        }
                }
        });

        // Detect action-oriented messages
        if (message.sender === "user" && /\?$/.test(text.trim())) {
                actions.push({ type: "question", preview: text.slice(0, 100) });
        }
        if (/please|can you|could you|help|show|check|find|tell/i.test(text)) {
                actions.push({ type: "request", preview: text.slice(0, 100) });
        }

        return {
                topics: Array.from(topics),
                animals: Array.from(animals),
                groups: Array.from(groups),
                actions,
        };
}

/**
 * Extracts key topics and entities from a conversation
 * @param {Array} messages - Array of message objects
 * @returns {Object} Aggregated topics, animals, groups, and key points
 */
export function extractConversationTopics(messages) {
        const allTopics = new Set();
        const allAnimals = new Set();
        const allGroups = new Set();
        const keyPoints = [];

        if (!Array.isArray(messages) || messages.length === 0) {
                return {
                        topics: [],
                        animals: [],
                        groups: [],
                        keyPoints: [],
                };
        }

        messages.forEach((message) => {
                const extracted = extractFromMessage(message);

                extracted.topics.forEach((t) => allTopics.add(t));
                extracted.animals.forEach((a) => allAnimals.add(a));
                extracted.groups.forEach((g) => allGroups.add(g));

                // Capture user questions as key points
                if (message.sender === "user" && extracted.actions.some((a) => a.type === "question")) {
                        const preview = (message.content || message.text || "").slice(0, 80);
                        keyPoints.push(`Q: ${preview}${preview.length >= 80 ? "..." : ""}`);
                }
        });

        return {
                topics: Array.from(allTopics),
                animals: Array.from(allAnimals).slice(0, SUMMARIZER_CONFIG.MAX_TRACKED_ANIMALS),
                groups: Array.from(allGroups),
                keyPoints: keyPoints.slice(0, SUMMARIZER_CONFIG.MAX_KEY_POINTS),
        };
}

/**
 * Generates a condensed summary of older conversation
 * @param {Array} messages - Array of message objects to summarize
 * @param {Object} existingSummary - Previous summary to merge with (optional)
 * @returns {Object|null} Summary object or null if not enough messages
 */
export function summarizeConversation(messages, existingSummary = null) {
        if (!Array.isArray(messages) || messages.length < SUMMARIZER_CONFIG.SUMMARY_THRESHOLD) {
                return existingSummary;
        }

        const extracted = extractConversationTopics(messages);

        // Merge with existing summary if provided
        const mergedTopics = new Set([
                ...extracted.topics,
                ...(existingSummary?.topics || []),
        ]);

        const mergedAnimals = new Set([
                ...extracted.animals,
                ...(existingSummary?.referencedAnimals || []),
        ]);

        const mergedKeyPoints = [
                ...(existingSummary?.keyPoints || []),
                ...extracted.keyPoints,
        ].slice(-SUMMARIZER_CONFIG.MAX_KEY_POINTS);

        return {
                timestamp: Date.now(),
                topics: Array.from(mergedTopics),
                referencedAnimals: Array.from(mergedAnimals).slice(0, SUMMARIZER_CONFIG.MAX_TRACKED_ANIMALS),
                groups: extracted.groups,
                keyPoints: mergedKeyPoints,
                messageCount: messages.length + (existingSummary?.messageCount || 0),
        };
}

/**
 * Formats summary for inclusion in system prompt
 * @param {Object} summary - Summary object from summarizeConversation
 * @param {string} language - Language code (en, de, etc.)
 * @returns {string} Formatted summary string for prompt injection
 */
export function formatSummaryForPrompt(summary, language = "en") {
        if (!summary) {
                return "";
        }

        const isGerman = language.startsWith("de") || language === "ger";
        const parts = [];

        // Header
        parts.push(isGerman ? "VORHERIGER KONVERSATIONSKONTEXT:" : "PREVIOUS CONVERSATION CONTEXT:");

        // Topics discussed
        if (summary.topics && summary.topics.length > 0) {
                const topicLabel = isGerman ? "Besprochene Themen" : "Topics discussed";
                parts.push(`${topicLabel}: ${summary.topics.join(", ")}`);
        }

        // Referenced animals
        if (summary.referencedAnimals && summary.referencedAnimals.length > 0) {
                const animalLabel = isGerman ? "Erwähnte Tiere" : "Animals mentioned";
                const animalList = summary.referencedAnimals.slice(0, 5).join(", ");
                const moreCount = summary.referencedAnimals.length - 5;
                const suffix = moreCount > 0 ? (isGerman ? ` (+${moreCount} weitere)` : ` (+${moreCount} more)`) : "";
                parts.push(`${animalLabel}: ${animalList}${suffix}`);
        }

        // Groups mentioned
        if (summary.groups && summary.groups.length > 0) {
                const groupLabel = isGerman ? "Erwähnte Gruppen" : "Groups mentioned";
                parts.push(`${groupLabel}: ${summary.groups.join(", ")}`);
        }

        // Key points from conversation
        if (summary.keyPoints && summary.keyPoints.length > 0) {
                const keyPointsLabel = isGerman ? "Wichtige Punkte" : "Key points";
                parts.push(`${keyPointsLabel}:`);
                summary.keyPoints.forEach((point) => {
                        parts.push(`  - ${point}`);
                });
        }

        return parts.join("\n");
}

/**
 * Determines if summarization should be triggered
 * @param {number} messageCount - Current message count
 * @returns {boolean} Whether to trigger summarization
 */
export function shouldSummarize(messageCount) {
        return messageCount >= SUMMARIZER_CONFIG.SUMMARY_THRESHOLD;
}

/**
 * Gets the number of recent messages to keep in full detail
 * @returns {number} Number of messages to keep
 */
export function getRecentMessagesToKeep() {
        return SUMMARIZER_CONFIG.RECENT_MESSAGES_TO_KEEP;
}

const conversationSummarizer = {
        extractConversationTopics,
        summarizeConversation,
        formatSummaryForPrompt,
        shouldSummarize,
        getRecentMessagesToKeep,
};

export default conversationSummarizer;
